import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import { Copy, Download, Smartphone, Check, Trash2, ArrowLeft, Link2 } from "lucide-react";

const PHONE_URL = `${window.location.origin}/sl/phone`;

function Copyable({ text, label }) {
  return <button onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied"); }} className="inline-flex items-center gap-1.5 surface-2 rounded-lg px-3 py-2 text-xs font-mono break-all"><Copy size={13} /> {label || text}</button>;
}

export default function SLSetup() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [code, setCode] = useState(sp.get("code") || "");
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadDevices = () => { if (user) api.get("/sl/v1/devices").then(({ data }) => setDevices(data)).catch(() => {}); };
  useEffect(() => { loadDevices(); }, [user]); // eslint-disable-line

  const approve = async () => { if (!code.trim()) return; setBusy(true); try { await api.post("/sl/v1/pair/approve", { code }); toast.success("Device approved — your phone will connect shortly"); setCode(""); loadDevices(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const deny = async () => { try { await api.post("/sl/v1/pair/deny", { code }); toast.success("Denied"); setCode(""); } catch (e) { toast.error(errText(e)); } };
  const revoke = async (id) => { try { await api.post(`/sl/v1/device/${id}/revoke`); loadDevices(); toast.success("Device disconnected"); } catch (e) { toast.error(errText(e)); } };

  const Step = ({ n, children }) => <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-eblue/15 text-eblue text-xs flex items-center justify-center font-mono">{n}</span><span className="text-sm text-silver">{children}</span></li>;

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <header className="sticky top-0 z-40 glass border-b border-white/8"><div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between"><Logo /><Link to="/" className="text-sm text-muted-foreground hover:text-white flex items-center gap-1"><ArrowLeft size={15} /> Home</Link></div></header>
      <main className="max-w-3xl mx-auto px-5 py-8 space-y-8">
        <div>
          <div className="flex items-center gap-2"><Smartphone className="text-gold" /><h1 className="font-display font-extrabold text-3xl">VirtuStars Phone</h1></div>
          <p className="text-sm text-muted-foreground mt-2">Wear a phone-shaped HUD in Second Life and browse VirtuStars on its screen via Shared Media. Build the 2-prim HUD below, drop in the script, and attach it.</p>
        </div>

        {/* Phone URL + downloads */}
        <section className="surface rounded-2xl p-5 space-y-3">
          <h2 className="font-display font-semibold">1 · Your phone URL & scripts</h2>
          <div className="flex items-center gap-2 flex-wrap"><span className="text-xs text-muted-foreground">Phone URL:</span><Copyable text={PHONE_URL} /></div>
          <div className="flex gap-2 flex-wrap">
            <a href="/sl/VirtuStars_Phone_Screen.lsl" download className="inline-flex items-center gap-1.5 btn-blue rounded-lg px-3 py-2 text-xs" data-testid="dl-screen-script"><Download size={14} /> VirtuStars_Phone_Screen.lsl</a>
            <a href="/sl/VirtuStars_Phone_Pairing.lsl" download className="inline-flex items-center gap-1.5 surface-2 rounded-lg px-3 py-2 text-xs" data-testid="dl-pair-script"><Download size={14} /> VirtuStars_Phone_Pairing.lsl (optional)</a>
          </div>
          <p className="text-[11px] text-muted-foreground">The screen script already contains your published phone URL. The pairing script is optional — add it after the screen works.</p>
        </section>

        {/* Build HUD */}
        <section className="surface rounded-2xl p-5">
          <h2 className="font-display font-semibold mb-3">2 · Build the 2-prim HUD</h2>
          <ol className="space-y-2.5">
            <Step n="1">Rez a box. Make the <b>Body</b>: size X 0.04, Y 0.24, Z 0.46 m. Rotation 0,0,0.</Step>
            <Step n="2">Rez a second box as the <b>Screen</b>: size X 0.01, Y 0.20, Z 0.40 m. Rotation 0,0,0. Color it white.</Step>
            <Step n="3">Screen Y and Z match the body. Set Screen <b>X position = Body X − 0.026 m</b> so it sits just in front.</Step>
            <Step n="4">Put <b>VirtuStars_Phone_Screen.lsl</b> inside the Screen prim.</Step>
            <Step n="5">Select the <b>Screen first</b>, then shift-select the <b>Body last</b>, and Link.</Step>
            <Step n="6">Take the linked object into inventory, then <b>Attach → HUD Center</b> and reposition to taste.</Step>
            <Step n="7">The script sets media on face <b>4</b> (default for an unrotated box) at 512×1024. If your screen faces a different way, change SCREEN_FACE in the script.</Step>
          </ol>
          <p className="text-[11px] text-muted-foreground mt-3">A handheld phone model and a holding animation are separate optional additions.</p>
        </section>

        {/* Enable media */}
        <section className="surface rounded-2xl p-5">
          <h2 className="font-display font-semibold mb-2">3 · Enable media & troubleshooting</h2>
          <ul className="text-sm text-silver space-y-1.5 list-disc pl-5">
            <li>In your viewer: Preferences → Sound & Media → enable <b>Media</b> and <b>auto-play</b> (or click the play ▶ on the HUD).</li>
            <li>Tap the screen once to (re)load the app (first-click interaction, owner-only).</li>
            <li>Blank screen? Confirm the face is 4, media is enabled, and the URL loads in your normal browser.</li>
            <li>Audio/video needs a tap first (viewer requires a user gesture).</li>
            <li>For uploads and complex editing, use “Open full VirtuStars website”.</li>
          </ul>
        </section>

        {/* Connect device */}
        <section className="surface rounded-2xl p-5">
          <h2 className="font-display font-semibold mb-2 flex items-center gap-2"><Link2 size={16} className="text-eblue" /> 4 · Connect your phone (pairing)</h2>
          {!user ? <p className="text-sm text-muted-foreground">Sign in to VirtuStars to approve a device. <Link to="/login" className="text-eblue">Log in →</Link></p> : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">On the phone HUD, tap “Connect via pairing code”. Enter the 6-character code here and approve.</p>
              <div className="flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABC123" maxLength={6} className="surface-2 border border-white/10 rounded-lg px-3 h-11 text-sm font-mono tracking-widest w-32 outline-none" data-testid="setup-code-input" />
                <button onClick={approve} disabled={busy} className="btn-gold rounded-lg px-4 h-11 text-sm" data-testid="setup-approve-btn"><Check size={15} className="inline mr-1" /> Approve</button>
                <button onClick={deny} className="surface-2 rounded-lg px-4 h-11 text-sm" data-testid="setup-deny-btn">Deny</button>
              </div>
            </div>
          )}
        </section>

        {/* Connected devices */}
        {user && (
          <section className="surface rounded-2xl p-5">
            <h2 className="font-display font-semibold mb-3">Connected Devices</h2>
            {devices.length === 0 ? <p className="text-sm text-muted-foreground">No connected devices yet.</p> : (
              <div className="space-y-2">{devices.map((d) => (
                <div key={d.id} className="flex items-center gap-3 surface-2 rounded-lg p-3" data-testid={`device-${d.id}`}>
                  <Smartphone size={18} className="text-eblue" />
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium">{d.name}</div><div className="text-[11px] text-muted-foreground">Connected {new Date(d.connected_at).toLocaleString()}</div></div>
                  <button onClick={() => revoke(d.id)} className="text-rose-400 flex items-center gap-1 text-xs" data-testid={`revoke-${d.id}`}><Trash2 size={14} /> Disconnect</button>
                </div>
              ))}</div>
            )}
          </section>
        )}
        <div className="text-center"><a href="/sl/phone" className="text-eblue text-sm">Open the VirtuStars Phone →</a></div>
      </main>
    </div>
  );
}
